import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Pencil, Check, X, Loader2, CalendarRange, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

// ── Constantes ────────────────────────────────────────────────────────────────

const STATUTS = {
  actif:   { label: 'Actif',   bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
  cloture: { label: 'Clôturé', bg: '#F8FAFC', color: '#64748B', border: '#E2E8F0' },
};

const EMPTY_FORM = { societe_id: '', libelle: '', date_debut: '', date_fin: '', statut: 'actif' };

// ── Badge statut ──────────────────────────────────────────────────────────────

function StatutBadge({ statut }) {
  const s = STATUTS[statut] || STATUTS.cloture;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
}

// ── Formulaire création / édition ─────────────────────────────────────────────

function ExerciceForm({ initial, societes, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const valid = form.societe_id && form.libelle.trim() && form.date_debut && form.date_fin
    && form.date_fin >= form.date_debut;

  return (
    <div className="rounded-2xl border p-4 space-y-3 mb-4"
      style={{ borderColor: 'var(--primary-solid)', background: '#FAFBFF' }}>
      <div className="grid grid-cols-2 gap-3">
        {/* Société */}
        <div className="space-y-1 col-span-2 sm:col-span-1">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Société *</label>
          <select
            value={form.societe_id}
            onChange={e => set('societe_id', e.target.value)}
            disabled={!!initial}
            data-testid="exc-form-societe"
            className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2 bg-white disabled:opacity-60"
            style={{ borderColor: 'var(--border)' }}>
            <option value="">— Choisir une société —</option>
            {societes.map(s => (
              <option key={s.societe_id} value={s.societe_id}>{s.nom}</option>
            ))}
          </select>
        </div>
        {/* Libellé */}
        <div className="space-y-1 col-span-2 sm:col-span-1">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Libellé *</label>
          <input
            autoFocus
            value={form.libelle}
            onChange={e => set('libelle', e.target.value)}
            placeholder="ex: Exercice 2025"
            data-testid="exc-form-libelle"
            className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2"
            style={{ borderColor: 'var(--border)' }}
          />
        </div>
        {/* Date début */}
        <div className="space-y-1">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Date de début *</label>
          <input
            type="date"
            value={form.date_debut}
            onChange={e => set('date_debut', e.target.value)}
            data-testid="exc-form-date-debut"
            className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2"
            style={{ borderColor: 'var(--border)' }}
          />
        </div>
        {/* Date fin */}
        <div className="space-y-1">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Date de fin *</label>
          <input
            type="date"
            value={form.date_fin}
            onChange={e => set('date_fin', e.target.value)}
            data-testid="exc-form-date-fin"
            className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2"
            style={{ borderColor: 'var(--border)' }}
          />
        </div>
        {/* Statut */}
        <div className="space-y-1 col-span-2 sm:col-span-1">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Statut</label>
          <select
            value={form.statut}
            onChange={e => set('statut', e.target.value)}
            data-testid="exc-form-statut"
            className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2 bg-white"
            style={{ borderColor: 'var(--border)' }}>
            <option value="actif">Actif</option>
            <option value="cloture">Clôturé</option>
          </select>
        </div>
      </div>
      {/* Erreur de cohérence dates */}
      {form.date_debut && form.date_fin && form.date_fin < form.date_debut && (
        <p className="text-xs" style={{ color: '#DC2626' }}>
          La date de fin doit être postérieure à la date de début.
        </p>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm border hover:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <X size={13} /> Annuler
        </button>
        <button type="button" onClick={() => onSave(form)} disabled={!valid || saving}
          data-testid="exc-form-save"
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
          style={{ background: 'var(--primary-gradient)' }}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function ExercicesComptables() {
  const [exercices, setExercices]   = useState([]);
  const [societes,  setSocietes]    = useState([]);
  const [loading,   setLoading]     = useState(true);
  const [creating,  setCreating]    = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [saving,    setSaving]      = useState(false);
  const [deleting,  setDeleting]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [exR, socR] = await Promise.all([
        api.getExercicesComptables(),
        api.getSocietes(),
      ]);
      setExercices(exR.data || []);
      setSocietes(socR.data || []);
    } catch {
      toast.error('Erreur chargement exercices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await api.createExerciceComptable(form);
      toast.success('Exercice créé');
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (exercice_id, form) => {
    setSaving(true);
    try {
      await api.updateExerciceComptable(exercice_id, form);
      toast.success('Exercice mis à jour');
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatut = async (exc) => {
    const newStatut = exc.statut === 'actif' ? 'cloture' : 'actif';
    try {
      await api.updateExerciceComptable(exc.exercice_id, { statut: newStatut });
      toast.success(newStatut === 'actif' ? 'Exercice réactivé' : 'Exercice clôturé');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (exercice_id) => {
    setDeleting(exercice_id);
    try {
      await api.deleteExerciceComptable(exercice_id);
      toast.success('Exercice supprimé');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    } finally {
      setDeleting(null);
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, j] = String(d).slice(0, 10).split('-');
    return `${j}/${m}/${y}`;
  };

  // Grouper par société
  const bySociete = societes.map(s => ({
    ...s,
    exercices: exercices.filter(e => e.societe_id === s.societe_id),
  })).filter(s => s.exercices.length > 0 || societes.length <= 5);

  // Toutes les sociétés pour le formulaire
  const allSocietes = societes;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
            Exercices comptables
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Définissez les périodes comptables par société. Les relevés de compte utilisent
            automatiquement les exercices actifs pour borner les données affichées.
          </p>
        </div>
        <button
          onClick={() => { setCreating(true); setEditingId(null); }}
          data-testid="exc-btn-creer"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white whitespace-nowrap"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={15} /> Nouvel exercice
        </button>
      </div>

      {/* Formulaire de création */}
      {creating && (
        <ExerciceForm
          societes={allSocietes}
          onSave={handleCreate}
          onCancel={() => setCreating(false)}
          saving={saving}
        />
      )}

      {/* Contenu */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={22} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
        </div>
      ) : exercices.length === 0 && !creating ? (
        <div className="rounded-2xl border border-dashed p-10 text-center"
          style={{ borderColor: 'var(--border)' }}>
          <CalendarRange size={32} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-medium" style={{ color: 'var(--text-secondary)' }}>Aucun exercice défini</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Créez un exercice comptable pour chaque société afin de filtrer les relevés de compte.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {bySociete.map(soc => (
            <div key={soc.societe_id} className="rounded-2xl border overflow-hidden"
              style={{ borderColor: 'var(--border)' }}>
              {/* Header société */}
              <div className="px-4 py-3 flex items-center gap-2"
                style={{ background: 'var(--surface-alt, #FAFAF8)', borderBottom: '1px solid var(--border)' }}>
                <CalendarRange size={15} style={{ color: 'var(--primary-solid)' }} />
                <span className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {soc.nom}
                </span>
                <span className="text-xs ml-1" style={{ color: 'var(--text-muted)' }}>
                  {soc.exercices.length} exercice{soc.exercices.length > 1 ? 's' : ''}
                </span>
              </div>

              {/* Tableau des exercices */}
              {soc.exercices.length > 0 && (
                <table className="w-full">
                  <thead>
                    <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border-light)' }}>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Libellé</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Début</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Fin</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Statut</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soc.exercices.map(exc => (
                      <React.Fragment key={exc.exercice_id}>
                        {editingId === exc.exercice_id ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-3">
                              <ExerciceForm
                                initial={{
                                  societe_id: exc.societe_id,
                                  libelle:    exc.libelle,
                                  date_debut: String(exc.date_debut).slice(0, 10),
                                  date_fin:   String(exc.date_fin).slice(0, 10),
                                  statut:     exc.statut,
                                }}
                                societes={allSocietes}
                                onSave={(form) => handleUpdate(exc.exercice_id, form)}
                                onCancel={() => setEditingId(null)}
                                saving={saving}
                              />
                            </td>
                          </tr>
                        ) : (
                          <tr className="border-b last:border-0 hover:bg-gray-50 transition-colors"
                            style={{ borderColor: 'var(--border-light)' }}
                            data-testid={`exc-row-${exc.exercice_id}`}>
                            <td className="px-4 py-3 text-sm font-medium" style={{ color: 'var(--text)' }}>
                              {exc.libelle}
                            </td>
                            <td className="px-4 py-3 text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                              {fmtDate(exc.date_debut)}
                            </td>
                            <td className="px-4 py-3 text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                              {fmtDate(exc.date_fin)}
                            </td>
                            <td className="px-4 py-3">
                              <StatutBadge statut={exc.statut} />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1.5">
                                {/* Toggle actif/cloturé */}
                                <button
                                  onClick={() => handleToggleStatut(exc)}
                                  data-testid={`exc-toggle-${exc.exercice_id}`}
                                  title={exc.statut === 'actif' ? 'Clôturer' : 'Réactiver'}
                                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                                  style={{ color: exc.statut === 'actif' ? '#15803D' : '#94A3B8' }}>
                                  {exc.statut === 'actif'
                                    ? <ToggleRight size={17} />
                                    : <ToggleLeft  size={17} />}
                                </button>
                                {/* Éditer */}
                                <button
                                  onClick={() => { setEditingId(exc.exercice_id); setCreating(false); }}
                                  data-testid={`exc-edit-${exc.exercice_id}`}
                                  className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                                  style={{ color: '#3B82F6' }}>
                                  <Pencil size={14} />
                                </button>
                                {/* Supprimer */}
                                <button
                                  onClick={() => handleDelete(exc.exercice_id)}
                                  disabled={deleting === exc.exercice_id}
                                  data-testid={`exc-delete-${exc.exercice_id}`}
                                  className="p-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
                                  style={{ color: '#EF4444' }}>
                                  {deleting === exc.exercice_id
                                    ? <Loader2 size={14} className="animate-spin" />
                                    : <Trash2 size={14} />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
